import { useEffect, useState } from "react";



export default function ArqHero() {
  const [activeRow, setActiveRow] = useState(0);

  useEffect(() => {
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) return;
    const id = setInterval(() => setActiveRow((i) => (i + 1) % 6), 2000);
    return () => clearInterval(id);
  }, []);

  return (
    <section className="arq-radial-tl relative min-h-screen flex items-center pt-32 md:pt-32 pb-20 px-6 overflow-hidden">
      {/* Subtle right-side grid */}
      <div
        aria-hidden
        className="hidden md:block absolute right-0 top-0 bottom-0 w-[45%] opacity-[0.06] pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(hsl(var(--arq-blue)) 1px, transparent 1px), linear-gradient(90deg, hsl(var(--arq-blue)) 1px, transparent 1px)",
          backgroundSize: "48px 48px",
          maskImage: "radial-gradient(ellipse at 70% 50%, black 30%, transparent 75%)",
        }}
      />

      <div className="max-w-[1200px] mx-auto w-full relative z-10">
        <div className="grid md:grid-cols-[55%_40%] md:gap-[5%] items-center">
          <div>
            <div
              className="arq-label mb-6 opacity-0 animate-[fade-in_600ms_cubic-bezier(0.16,1,0.32,1)_forwards]"
              style={{ animationDelay: "0ms" }}
            >
              Operational AI Security for Regulated AI Systems
            </div>

            <h1
              className="font-display font-bold text-arq-text leading-[1.1] tracking-[-0.02em] mb-6 opacity-0 animate-[fade-in_600ms_cubic-bezier(0.16,1,0.32,1)_forwards]"
              style={{ fontSize: "clamp(40px, 6.5vw, 64px)", animationDelay: "0ms" }}
            >
              Secure and Validate the AI <span className="text-arq-blue">Already Moving Through Your Business.</span>
            </h1>

            <p
              className="text-arq-muted mb-4 max-w-[620px] opacity-0 animate-[fade-in_600ms_cubic-bezier(0.16,1,0.32,1)_forwards]"
              style={{ fontSize: "clamp(17px, 1.6vw, 19px)", lineHeight: 1.6, animationDelay: "150ms" }}
            >
              ArqeraAI helps regulated and high-stakes organizations assess, remediate, and validate
              AI, application, and privacy risk across production systems, agents, data flows,
              vendors, and workflows, before those risks become regulatory issues, stalled
              enterprise deals, customer trust failures, cyber insurance friction, or security
              incidents.
            </p>

            <p
              className="text-arq-text/90 text-[15.5px] font-semibold mb-6 opacity-0 animate-[fade-in_600ms_cubic-bezier(0.16,1,0.32,1)_forwards]"
              style={{ animationDelay: "220ms" }}
            >
              Find what is exposed. Fix what matters. Prove what is secure.
            </p>

            <div
              className="flex flex-col sm:flex-row sm:items-center gap-4 mb-6 opacity-0 animate-[fade-in_600ms_cubic-bezier(0.16,1,0.32,1)_forwards]"
              style={{ animationDelay: "300ms" }}
            >
              <button
                onClick={() => document.querySelector("#contact")?.scrollIntoView({ behavior: "smooth" })}
                className="bg-arq-blue text-white font-semibold text-[15px] px-8 py-4 rounded-md hover:bg-arq-blue-bright hover:shadow-[0_0_24px_rgba(26,111,212,0.35)] transition-all duration-150 tracking-[0.01em] text-center"
              >
                Book a Readiness Review
              </button>
              <button
                onClick={() => document.querySelector("#how-it-works")?.scrollIntoView({ behavior: "smooth" })}
                className="bg-transparent text-arq-text font-medium text-[15px] px-2 py-4 hover:underline underline-offset-4 transition-all duration-150"
              >
                See the Methodology
              </button>
            </div>

            <div
              className="flex flex-wrap items-center gap-x-2 gap-y-2 text-[13px] text-arq-tertiary opacity-0 animate-[fade-in_600ms_cubic-bezier(0.16,1,0.32,1)_forwards]"
              style={{ animationDelay: "450ms" }}
            >
              <span>Fixed scope</span>
              <span>·</span>
              <span>Written proposal in 48 hours</span>
              <span>·</span>
              <span>Evidence-ready deliverables</span>
            </div>
          </div>

          {/* Right column - Agentic AI Readiness Scorecard */}
          <div
            className="hidden md:block opacity-0 animate-[fade-in_600ms_cubic-bezier(0.16,1,0.32,1)_forwards]"
            style={{ animationDelay: "450ms" }}
          >
            <div className="relative bg-arq-surface1 border border-arq-border rounded-xl p-6 shadow-[0_8px_40px_rgba(0,0,0,0.4)] overflow-hidden">
              <div
                aria-hidden
                className="pointer-events-none absolute inset-x-0 h-px bg-gradient-to-r from-transparent via-arq-blue/60 to-transparent motion-safe:animate-[arq-scan_4.5s_linear_infinite]"
                style={{ top: 0 }}
              />
              <div className="flex items-center justify-between mb-5">
                <div className="flex items-center gap-2 font-mono text-[11px] tracking-[0.12em] uppercase text-arq-muted">
                  <span className="relative flex h-1.5 w-1.5">
                    <span className="absolute inset-0 rounded-full bg-arq-teal motion-safe:animate-ping opacity-75" />
                    <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-arq-teal" />
                  </span>
                  Operational AI Security Scorecard
                </div>
                <div className="font-mono text-[10px] text-arq-tertiary tracking-[0.08em]">
                  AGT-████
                </div>
              </div>

              <div className="space-y-2">
                {[
                  { id: "TOOL", sev: "AT RISK", sevColor: "#C8402A", title: "Tool Access", meta: "Agent has unscoped CRM and API privileges" },
                  { id: "RAG", sev: "VALIDATE", sevColor: "#D97706", title: "Data Retrieval", meta: "RAG sources include untrusted inputs" },
                  { id: "HITL", sev: "PARTIAL", sevColor: "#D97706", title: "Human Approval", meta: "Approval gates missing on 2 of 5 actions" },
                  { id: "LOG", sev: "MISSING", sevColor: "#C8402A", title: "Logging & Evidence", meta: "Tool calls not captured for audit" },
                  { id: "PI", sev: "AT RISK", sevColor: "#C8402A", title: "Prompt Injection", meta: "Indirect injection path via shared docs" },
                  { id: "CRIT", sev: "HIGH", sevColor: "hsl(var(--arq-teal))", title: "Business Criticality", meta: "Controls required before expansion" },
                ].map((row, i) => {
                  const isActive = activeRow === i;
                  return (
                    <div
                      key={row.id}
                      className={`p-2.5 rounded-md bg-arq-bg/60 border transition-all duration-500 ${
                        isActive
                          ? "border-arq-blue/60 shadow-[0_0_0_1px_hsl(var(--arq-blue)/0.25),0_0_24px_-4px_hsl(var(--arq-blue)/0.45)] translate-x-[2px]"
                          : "border-arq-border"
                      }`}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span
                          className="font-mono text-[10px] tracking-[0.08em] px-1.5 py-0.5 rounded"
                          style={{
                            color: row.sevColor,
                            background: `${row.sevColor}1A`,
                            border: `1px solid ${row.sevColor}40`,
                          }}
                        >
                          {row.sev}
                        </span>
                        <span className="font-mono text-[10px] text-arq-tertiary tracking-[0.06em]">
                          {row.id}
                        </span>
                      </div>
                      <div className="text-arq-text text-[13px] leading-tight mb-0.5">
                        {row.title}
                      </div>
                      <div className="font-mono text-[10px] text-arq-muted tracking-[0.03em]">
                        {row.meta}
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="mt-4 pt-3 border-t border-arq-border">
                <div className="flex items-center justify-between mb-1">
                  <span className="font-mono text-[10.5px] text-arq-tertiary tracking-[0.06em] uppercase">Status</span>
                  <span className="font-mono text-[10.5px] text-arq-blue font-semibold tracking-[0.06em]">REMEDIATE → VALIDATE</span>
                </div>
                <div className="font-mono text-[10px] text-arq-tertiary tracking-[0.05em] flex items-center justify-between">
                  <span>ArqeraAI Readiness Review · 3 priority controls · evidence package</span>
                  <span className="text-arq-teal motion-safe:animate-pulse">●</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
